import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Pencil, Trash2, Truck, ExternalLink, Phone, Mail } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const emptyForm = { name: '', contact_name: '', email: '', phone: '', address: '', postal_code: '', city: '', country: 'Suisse', website: '', notes: '' };

const SuppliersPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showDetail, setShowDetail] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/suppliers/`, { params: { search } });
      setSuppliers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => { setEditItem(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (s) => { setEditItem(s); setForm({ ...s }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.name) { toast.error('Le nom est requis'); return; }
    try {
      if (editItem) {
        await axios.put(`${API}/suppliers/${editItem.id}`, form);
        toast.success('Fournisseur modifie');
      } else {
        await axios.post(`${API}/suppliers/`, form);
        toast.success('Fournisseur ajoute');
      }
      setShowDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Desactiver ce fournisseur ?')) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success('Fournisseur desactive');
      loadData();
    } catch (err) { toast.error('Erreur'); }
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/suppliers/${id}`);
      setShowDetail(res.data);
    } catch (err) { toast.error('Erreur chargement'); }
  };

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="suppliers-title">{t('suppliers.title')}</h1>
            <p className="text-base text-slate-600 mt-1">Gestion des fournisseurs de pieces et pneus</p>
          </div>
          <Button onClick={openAdd} data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter fournisseur
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom, contact, email..." className="pl-10" data-testid="supplier-search" />
        </div>

        {/* Cards */}
        {loading ? (
          <div className="text-slate-400 text-center py-20">{t('common.loading')}</div>
        ) : suppliers.length === 0 ? (
          <Card className="p-12 text-center">
            <Truck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun fournisseur</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suppliers.map((s) => (
              <Card key={s.id} className="p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadDetail(s.id)} data-testid={`supplier-card-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900 truncate">{s.name}</h3>
                    {s.contact_name && <p className="text-sm text-slate-600">{s.contact_name}</p>}
                    <p className="text-xs text-slate-400 mt-1">{s.city}{s.city && s.country ? ', ' : ''}{s.country}</p>
                  </div>
                  <div className="flex gap-1 shrink-0 ml-2">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(s); }} className="text-slate-400 hover:text-blue-600">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex gap-3 text-xs text-slate-500">
                  {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>}
                  {s.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</span>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Modifier fournisseur' : 'Nouveau fournisseur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs text-slate-500">Nom *</Label>
              <Input value={form.name} onChange={f('name')} className="mt-1" data-testid="supplier-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Contact</Label>
                <Input value={form.contact_name} onChange={f('contact_name')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Telephone</Label>
                <Input value={form.phone} onChange={f('phone')} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input value={form.email} onChange={f('email')} type="email" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Adresse</Label>
              <Input value={form.address} onChange={f('address')} className="mt-1" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500">NPA</Label>
                <Input value={form.postal_code} onChange={f('postal_code')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Ville</Label>
                <Input value={form.city} onChange={f('city')} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Pays</Label>
                <Input value={form.country} onChange={f('country')} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Site web</Label>
              <Input value={form.website} onChange={f('website')} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes</Label>
              <textarea value={form.notes} onChange={f('notes')} className="w-full border rounded-lg p-2 text-sm h-16 mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} data-testid="save-supplier-btn">{editItem ? 'Modifier' : 'Ajouter'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" /> {showDetail.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-slate-500">Contact:</span> <span className="ml-1">{showDetail.contact_name || '-'}</span></div>
                  <div><span className="text-slate-500">Tel:</span> <span className="ml-1">{showDetail.phone || '-'}</span></div>
                  <div><span className="text-slate-500">Email:</span> <span className="ml-1">{showDetail.email || '-'}</span></div>
                  <div><span className="text-slate-500">Ville:</span> <span className="ml-1">{showDetail.city || '-'}</span></div>
                  <div className="col-span-2"><span className="text-slate-500">Adresse:</span> <span className="ml-1">{showDetail.address || '-'} {showDetail.postal_code}</span></div>
                  {showDetail.website && (
                    <div className="col-span-2"><span className="text-slate-500">Web:</span> <a href={showDetail.website} target="_blank" rel="noreferrer" className="ml-1 text-blue-600 hover:underline inline-flex items-center gap-1">{showDetail.website} <ExternalLink className="w-3 h-3" /></a></div>
                  )}
                </div>
                {showDetail.notes && <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{showDetail.notes}</p>}
                {/* Recent orders */}
                {showDetail.orders && showDetail.orders.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Commandes recentes</p>
                    <div className="space-y-1">
                      {showDetail.orders.map(o => (
                        <div key={o.id} className="flex justify-between text-sm bg-slate-50 rounded px-3 py-2">
                          <span className="font-medium">{o.number}</span>
                          <span className="text-slate-600">CHF {o.subtotal?.toFixed(2)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === 'recu' ? 'bg-green-100 text-green-700' : o.status === 'commande' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{o.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default SuppliersPage;
